/* Patch 24.1 — Governance Analytics + Block Heatmap
 * ------------------------------------------------------
 * CFO-grade view of Universal Posting Gate decisions over the
 * last N days. Built on top of /api/governance-analytics/*.
 *
 * Layout:
 *   [ KPI strip × 6 ]
 *   [ Block Heatmap   ]   [ Top Reasons (bar) ]
 *   [ Teams Impacted  ]   [ Resolution Time   ]
 *
 * Every interactive element carries a data-testid for the testing
 * agent and the rest of the system to introspect.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, ShieldAlert, AlertOctagon, Activity, Users,
  Clock, RefreshCw, DollarSign, Flame, CheckCircle2,
} from 'lucide-react';

const ARROW = '·';

const fmtMoney = (n) => `SAR ${(Number(n) || 0).toLocaleString('en-US',
  { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtMinutes = (m) => {
  if (!m || m < 1) return '< 1m';
  if (m < 60)  return `${Math.round(m)}m`;
  if (m < 1440) return `${(m / 60).toFixed(1)}h`;
  return `${(m / 1440).toFixed(1)}d`;
};

const SEVERITY_BG = {
  critical: 'bg-rose-100 text-rose-700 border-rose-200',
  high:     'bg-amber-100 text-amber-700 border-amber-200',
  medium:   'bg-yellow-100 text-yellow-700 border-yellow-200',
  low:      'bg-slate-100 text-slate-700 border-slate-200',
};


export default function GovernanceAnalytics() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    summary: null, heatmap: null, topReasons: null,
    teams: null, resolution: null,
    openBlocks: null, sla: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [s, h, r, t, rt, ob, sla] = await Promise.all([
        axiosInstance.get(`/governance-analytics/summary?days=${days}`),
        axiosInstance.get(`/governance-analytics/heatmap?days=${days}`),
        axiosInstance.get(`/governance-analytics/top-reasons?days=${days}`),
        axiosInstance.get(`/governance-analytics/teams?days=${days}`),
        axiosInstance.get(`/governance-analytics/resolution-time?days=${days}`),
        axiosInstance.get(`/governance-analytics/open-blocks`),
        axiosInstance.get(`/governance-analytics/sla?sla_hours=24&window_days=${days}`),
      ]);
      setData({
        summary: s.data, heatmap: h.data, topReasons: r.data,
        teams: t.data, resolution: rt.data,
        openBlocks: ob.data, sla: sla.data,
      });
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6"
         data-testid="governance-analytics-page">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Patch 24.1
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            Governance Analytics
          </h1>
          <p className="text-sm text-slate-600">
            Where the Posting Gate is refusing writes — and why.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-slate-300 rounded overflow-hidden">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                data-testid={`gv-window-${d}d`}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs font-semibold ${
                  days === d
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            data-testid="gv-refresh-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </header>

      {!data.summary && loading ? (
        <div className="h-64 flex items-center justify-center text-slate-500">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <>
          <KpiStrip s={data.summary} rt={data.resolution} sla={data.sla} open={data.openBlocks} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Heatmap h={data.heatmap} />
            </div>
            <TopReasons r={data.topReasons} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TeamsImpacted t={data.teams} />
            <ResolutionPanel rt={data.resolution} sla={data.sla} />
          </div>
          {/* Patch 25 — Open Blocks queue */}
          <OpenBlocksPanel open={data.openBlocks} onResolved={load} />
        </>
      )}
    </div>
  );
}


function KpiStrip({ s, rt, sla, open: openData }) {
  if (!s) return null;
  const items = [
    { icon: ShieldAlert, label: 'Currently Open',     value: openData?.open_count ?? 0,    accent: 'rose',    testid: 'kpi-open-blocks' },
    { icon: AlertOctagon, label: 'Critical (window)', value: s.critical_blocks,            accent: 'red',     testid: 'kpi-critical-blocks' },
    { icon: DollarSign,  label: 'Open Blocked Value', value: fmtMoney(openData?.blocked_value || 0), accent: 'orange', testid: 'kpi-open-blocked-value' },
    { icon: Clock,       label: 'Real MTTR',          value: fmtMinutes(rt?.mttr_minutes), accent: 'sky',     testid: 'kpi-mttr' },
    { icon: Activity,    label: 'SLA 24h',            value: `${sla?.sla_compliance_pct ?? 0}%`, accent: 'emerald', testid: 'kpi-sla' },
    { icon: Flame,       label: 'Top Cause',          value: s.top_reason || '—',          accent: 'fuchsia', testid: 'kpi-top-cause' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
         data-testid="gv-kpi-strip">
      {items.map((it) => (
        <div key={it.label} data-testid={it.testid}
             className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
            <it.icon size={12} /> {it.label}
          </div>
          <div className="text-xl font-bold text-slate-900 truncate" title={String(it.value)}>
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}


function Heatmap({ h }) {
  const cellColor = (count, max) => {
    if (!count) return 'bg-slate-100';
    if (max <= 0) return 'bg-rose-200';
    const r = count / max;
    if (r > 0.75) return 'bg-rose-600 text-white';
    if (r > 0.50) return 'bg-rose-500 text-white';
    if (r > 0.25) return 'bg-rose-400 text-white';
    if (r > 0.10) return 'bg-rose-300';
    return 'bg-rose-200';
  };
  if (!h) return null;
  if (!h.rows.length) {
    return (
      <section className="bg-white rounded-lg border border-slate-200 p-6"
               data-testid="gv-heatmap-empty">
        <h3 className="font-semibold text-slate-900 mb-1">Block Heatmap</h3>
        <p className="text-sm text-slate-500">
          No blocked attempts in the last {h.window_days} days. The
          ledger is in a healthy state.
        </p>
      </section>
    );
  }
  return (
    <section className="bg-white rounded-lg border border-slate-200 p-4 overflow-x-auto"
             data-testid="gv-heatmap">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">Block Heatmap</h3>
          <div className="text-xs text-slate-500">
            {h.window_days} days {ARROW} {h.rows.length} reason codes {ARROW} max cell {h.max_cell}
          </div>
        </div>
      </header>
      <table className="text-xs border-collapse">
        <thead>
          <tr>
            <th className="text-right pr-2 font-semibold text-slate-600">Reason</th>
            {h.dates.map(d => (
              <th key={d} className="text-center px-1 font-mono text-[9px] text-slate-400">
                {d.slice(5)}
              </th>
            ))}
            <th className="px-2 text-slate-600">Total</th>
          </tr>
        </thead>
        <tbody>
          {h.rows.map(row => (
            <tr key={row.reason} data-testid={`gv-heatmap-row-${row.reason}`}>
              <td className="text-right pr-2 py-0.5 font-mono text-[10px] text-slate-700 whitespace-nowrap">
                {row.reason}
              </td>
              {row.cells.map(c => (
                <td key={c.date} className="p-0.5">
                  <div
                    title={`${c.date} · ${c.count}`}
                    className={`w-6 h-6 rounded text-[10px] font-bold flex items-center justify-center ${cellColor(c.count, h.max_cell)}`}
                  >
                    {c.count || ''}
                  </div>
                </td>
              ))}
              <td className="px-2 font-bold text-slate-900 text-center">{row.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}


function TopReasons({ r }) {
  const max = useMemo(() => Math.max(1, ...((r?.items) || []).map(i => i.count)), [r]);
  if (!r) return null;
  return (
    <section className="bg-white rounded-lg border border-slate-200 p-4"
             data-testid="gv-top-reasons">
      <h3 className="font-semibold text-slate-900 mb-3">Top Root Causes</h3>
      {(!r.items || !r.items.length) ? (
        <div className="text-sm text-slate-500">No blocks recorded.</div>
      ) : (
        <div className="space-y-2">
          {r.items.map(it => (
            <div key={it.reason}
                 data-testid={`gv-reason-${it.reason}`}>
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className="font-mono text-slate-700">{it.reason}</span>
                <span className="font-bold">{it.count}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-2 rounded-full ${
                    it.severity === 'critical' ? 'bg-rose-500' :
                    it.severity === 'high' ? 'bg-amber-500' :
                    'bg-slate-400'
                  }`}
                  style={{ width: `${(it.count / max) * 100}%` }}
                />
              </div>
              <span className={`inline-block mt-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${SEVERITY_BG[it.severity] || SEVERITY_BG.low}`}>
                {it.severity}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function TeamsImpacted({ t }) {
  if (!t) return null;
  const total = (t.items || []).reduce((a, b) => a + b.attempts, 0) || 1;
  return (
    <section className="bg-white rounded-lg border border-slate-200 p-4"
             data-testid="gv-teams">
      <h3 className="font-semibold text-slate-900 mb-3 inline-flex items-center gap-2">
        <Users size={14} /> Teams Impacted
      </h3>
      {(!t.items || !t.items.length) ? (
        <div className="text-sm text-slate-500">No team breakdown available.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th>Team</th>
              <th className="text-right">Attempts</th>
              <th className="text-right">Share</th>
              <th className="text-right">Blocked Value</th>
            </tr>
          </thead>
          <tbody>
            {t.items.map(it => (
              <tr key={it.team} data-testid={`gv-team-${it.team}`}
                  className="border-t border-slate-100">
                <td className="py-2 font-semibold text-slate-900">{it.team}</td>
                <td className="py-2 text-right font-mono">{it.attempts}</td>
                <td className="py-2 text-right text-slate-600">
                  {((it.attempts / total) * 100).toFixed(0)}%
                </td>
                <td className="py-2 text-right font-mono text-slate-700">
                  {fmtMoney(it.blocked_value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}


function ResolutionPanel({ rt, sla }) {
  if (!rt) return null;
  const realData = rt.source === 'block_states';
  return (
    <section className="bg-white rounded-lg border border-slate-200 p-4"
             data-testid="gv-resolution-time">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900 inline-flex items-center gap-2">
          <Clock size={14} /> Resolution Time
        </h3>
        <span
          data-testid="gv-rt-source"
          className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${
            realData
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}
          title={realData ? 'Computed from block_states.resolution_minutes' : 'Proxy: time-to-quiet (no resolved blocks yet)'}
        >
          {realData ? 'Real' : 'Proxy'}
        </span>
      </div>
      {rt.cluster_count === 0 ? (
        <div className="text-sm text-slate-500">
          No resolved blocks yet. Resolve an open block to populate
          real MTTR / SLA metrics.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div data-testid="gv-rt-mttr">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">MTTR (avg)</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {fmtMinutes(rt.mttr_minutes)}
              </div>
            </div>
            <div data-testid="gv-rt-p50">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">P50</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {fmtMinutes(rt.p50_minutes)}
              </div>
            </div>
            <div data-testid="gv-rt-p95">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">P95</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {fmtMinutes(rt.p95_minutes)}
              </div>
            </div>
          </div>
          {sla && (
            <div className="border-t border-slate-100 pt-3 grid grid-cols-3 gap-3"
                 data-testid="gv-sla-grid">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Resolved</div>
                <div className="text-lg font-bold text-slate-900 mt-1">{sla.resolved_count}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Resolution Rate</div>
                <div className="text-lg font-bold text-slate-900 mt-1">{sla.resolution_rate}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Inside {sla.sla_hours}h</div>
                <div className={`text-lg font-bold mt-1 ${
                  sla.sla_compliance_pct >= 90 ? 'text-emerald-600' :
                  sla.sla_compliance_pct >= 50 ? 'text-amber-600' : 'text-rose-600'
                }`}>
                  {sla.sla_compliance_pct}%
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <p className="text-[10px] text-slate-400 mt-3">
        {realData
          ? 'Patch 25 — real data from block_states.resolution_minutes.'
          : 'Falls back to proxy when no resolved blocks exist.'}
      </p>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Patch 25 — Open Blocks queue with manual-resolve action
// ─────────────────────────────────────────────────────────────────────
function OpenBlocksPanel({ open: data, onResolved }) {
  const [resolving, setResolving] = React.useState(null);
  if (!data) return null;
  const items = data.items || [];

  const handleResolve = async (id) => {
    setResolving(id);
    try {
      await axiosInstance.post(`/governance-analytics/blocks/${id}/manual-resolve`);
      toast.success('Block resolved.');
      onResolved && onResolved();
    } catch (e) {
      toast.error(`Resolve failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setResolving(null); }
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-4"
             data-testid="gv-open-blocks-panel">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-slate-900 inline-flex items-center gap-2">
            <ShieldAlert size={14} /> Currently Open Blocks
          </h3>
          <div className="text-xs text-slate-500 mt-0.5">
            {data.open_count} open · SAR {Number(data.blocked_value || 0).toLocaleString('en-US', {maximumFractionDigits: 0})} blocked value
          </div>
        </div>
      </header>
      {!items.length ? (
        <div data-testid="gv-open-blocks-empty"
             className="text-sm text-slate-500 italic py-6 text-center">
          ✓ No open blocks. The ledger is clean.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="py-2">Block</th>
              <th>Operation</th>
              <th>Reason</th>
              <th className="text-right">Value</th>
              <th>Age</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(b => (
              <tr key={b.id} data-testid={`gv-open-block-${b.id}`}
                  className="border-b border-slate-50">
                <td className="py-2 font-mono text-[10px] text-slate-600">
                  {b.id.slice(0, 8)}
                </td>
                <td className="text-slate-700">{b.operation}</td>
                <td>
                  {(b.reason_codes || []).slice(0, 2).map(c => (
                    <span key={c} className="inline-block mr-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                      {c}
                    </span>
                  ))}
                </td>
                <td className="text-right font-mono text-slate-700">
                  {fmtMoney(b.blocked_value)}
                </td>
                <td className="text-xs text-slate-500" title={b.blocked_at}>
                  {relAge(b.blocked_at)}
                </td>
                <td className="text-right">
                  <button
                    data-testid={`gv-resolve-${b.id}`}
                    onClick={() => handleResolve(b.id)}
                    disabled={resolving === b.id}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 rounded px-2 py-1 disabled:opacity-50"
                  >
                    {resolving === b.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                    Mark Resolved
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function relAge(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}
